/**
 * QRAccessDisplay - Display delegate access permissions after QR scan
 * Shows housing, dining, sessions, and special access in an easy-to-read format
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { 
  User, Building2, Utensils, Calendar, Star, Check, X, 
  Home, Coffee, Clock, MapPin, Shield, Gift, Users,
  Video, FileText, Armchair, PartyPopper
} from 'lucide-react';

const AccessBadge = ({ allowed, label }) => (
  <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
    allowed ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
  }`}>
    {allowed ? <Check className="h-4 w-4" /> : <X className="h-4 w-4" />}
    <span className="text-sm font-medium">{label}</span>
  </div>
);

const MealItem = ({ meal }) => (
  <div className={`flex items-center justify-between p-3 rounded-lg ${
    meal.redeemed 
      ? 'bg-gray-100 text-gray-500' 
      : meal.has_access 
        ? 'bg-green-50 border border-green-200' 
        : 'bg-red-50 border border-red-200'
  }`}>
    <div className="flex items-center gap-3">
      <Utensils className={`h-4 w-4 ${meal.redeemed ? 'text-gray-400' : meal.has_access ? 'text-green-600' : 'text-red-600'}`} />
      <div>
        <div className={`font-medium ${meal.redeemed ? 'line-through' : ''}`}>{meal.name}</div>
        <div className="text-xs text-gray-500">{meal.day} • {meal.type}</div>
      </div>
    </div>
    <div className="text-right">
      {meal.redeemed ? (
        <Badge variant="secondary" className="bg-gray-200">Redeemed</Badge>
      ) : meal.has_access ? (
        <Badge className="bg-green-500">Allowed</Badge>
      ) : (
        <Badge variant="destructive">No Access</Badge>
      )}
    </div>
  </div>
);

const QRAccessDisplay = ({ accessData, onClose }) => {
  if (!accessData || !accessData.success) {
    return (
      <Card className="max-w-lg mx-auto" data-testid="qr-access-display-error">
        <CardContent className="py-8 text-center">
          <X className="h-12 w-12 mx-auto text-red-500 mb-4" />
          <p className="text-lg font-medium text-red-600">Invalid QR Code</p>
          <p className="text-sm text-gray-500 mt-2">Could not retrieve delegate information</p>
        </CardContent>
      </Card>
    );
  }

  const { delegate, tier, check_in, housing, meals, sessions, special_access } = accessData;

  return (
    <div className="space-y-4 max-w-lg mx-auto" data-testid="qr-access-display">
      {/* Delegate Header */}
      <Card className={`overflow-hidden ${delegate.is_vip ? 'ring-2 ring-yellow-400' : ''}`}>
        <div className={`p-4 ${delegate.is_vip ? 'bg-gradient-to-r from-yellow-500 to-amber-500' : 'bg-gradient-to-r from-blue-500 to-indigo-600'} text-white`}>
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-xl font-bold flex items-center gap-2">
                {delegate.is_vip && <Star className="h-5 w-5 text-yellow-200" />}
                {delegate.name}
              </h2>
              {delegate.title && <p className="text-sm opacity-90">{delegate.title}</p>}
              {delegate.company && (
                <p className="text-sm opacity-90 flex items-center gap-1 mt-1">
                  <Building2 className="h-3 w-3" />
                  {delegate.company}
                </p>
              )}
            </div>
            <Badge className={`${delegate.is_vip ? 'bg-yellow-600' : 'bg-white/20'}`}>
              {tier.tier_name}
            </Badge>
          </div>
          
          {/* Check-in status */}
          <div className="mt-3 flex items-center gap-2">
            {check_in.checked_in ? (
              <Badge className="bg-green-500">
                <Check className="h-3 w-3 mr-1" /> Checked In
              </Badge>
            ) : (
              <Badge variant="outline" className="border-white/50 text-white">
                Not Checked In
              </Badge>
            )}
            {check_in.badge_printed && (
              <Badge className="bg-purple-500">Badge Printed</Badge>
            )}
          </div>
        </div>
      </Card>

      {/* Housing Access */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Home className="h-4 w-4 text-indigo-600" />
            Housing
          </CardTitle>
        </CardHeader>
        <CardContent>
          {housing.assigned ? (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-green-700 font-medium mb-2">
                <Check className="h-4 w-4" />
                Room Assigned
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-gray-500">Room:</span>
                  <span className="ml-2 font-medium">{housing.room_number}</span>
                </div>
                <div>
                  <span className="text-gray-500">Building:</span>
                  <span className="ml-2 font-medium">{housing.building_name || '-'}</span>
                </div>
                {housing.floor && (
                  <div>
                    <span className="text-gray-500">Floor:</span>
                    <span className="ml-2 font-medium">{housing.floor}</span>
                  </div>
                )}
                {housing.room_type && (
                  <div>
                    <span className="text-gray-500">Type:</span>
                    <span className="ml-2 font-medium capitalize">{housing.room_type}</span>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className={`rounded-lg p-4 ${housing.eligible ? 'bg-yellow-50 border border-yellow-200' : 'bg-gray-50 border border-gray-200'}`}>
              <div className="flex items-center gap-2">
                {housing.eligible ? (
                  <>
                    <Clock className="h-4 w-4 text-yellow-600" />
                    <span className="text-yellow-700 font-medium">Eligible - Pending Assignment</span>
                  </>
                ) : (
                  <>
                    <X className="h-4 w-4 text-gray-500" />
                    <span className="text-gray-600">Not Included in Tier</span>
                  </>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Meal Access */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Coffee className="h-4 w-4 text-orange-600" />
            Meals
            {tier.meals_included ? (
              <Badge className="ml-auto bg-green-500 text-xs">Included</Badge>
            ) : (
              <Badge variant="outline" className="ml-auto text-xs">Not Included</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {meals && meals.length > 0 ? (
            <div className="space-y-2">
              {meals.map((meal, idx) => (
                <MealItem key={meal.meal_id || idx} meal={meal} />
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500 text-center py-4">No meals configured</p>
          )}
        </CardContent>
      </Card>

      {/* Session Access */}
      {sessions && sessions.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Calendar className="h-4 w-4 text-purple-600" />
              Registered Sessions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {sessions.map((session, idx) => (
                <div key={session.session_id || idx} className="flex items-center justify-between p-3 bg-purple-50 border border-purple-200 rounded-lg">
                  <div>
                    <div className="font-medium text-purple-900">{session.title}</div>
                    {session.location && (
                      <div className="text-xs text-purple-600 flex items-center gap-1 mt-1">
                        <MapPin className="h-3 w-3" />
                        {session.location}
                      </div>
                    )}
                  </div>
                  <Badge className="bg-purple-500">Access</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Special Access Permissions */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Shield className="h-4 w-4 text-blue-600" />
            Special Access
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-2">
            <AccessBadge 
              allowed={special_access.vip_lounge} 
              label={<span className="flex items-center gap-1"><Star className="h-3 w-3" /> VIP Lounge</span>}
            />
            <AccessBadge 
              allowed={special_access.priority_seating} 
              label={<span className="flex items-center gap-1"><Armchair className="h-3 w-3" /> Priority Seating</span>}
            />
            <AccessBadge 
              allowed={special_access.networking_events} 
              label={<span className="flex items-center gap-1"><Users className="h-3 w-3" /> Networking</span>}
            />
            <AccessBadge 
              allowed={special_access.workshops} 
              label={<span className="flex items-center gap-1"><PartyPopper className="h-3 w-3" /> Workshops</span>}
            />
            <AccessBadge 
              allowed={special_access.materials_download} 
              label={<span className="flex items-center gap-1"><FileText className="h-3 w-3" /> Materials</span>}
            />
            <AccessBadge 
              allowed={special_access.recording_access} 
              label={<span className="flex items-center gap-1"><Video className="h-3 w-3" /> Recordings</span>}
            />
            <AccessBadge 
              allowed={special_access.swag_bag} 
              label={<span className="flex items-center gap-1"><Gift className="h-3 w-3" /> Swag Bag</span>}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default QRAccessDisplay;
